import { useTranslation } from "react-i18next";

import { Badge, EmptyState } from "../../../components/ui";
import type { CentinelaEvent } from "../../../features/centinela/types";
import { toCaseOrigin, toCaseUrgency } from "../services/mapCentinelaCase";
import {
  CASE_STATUS_TONES,
  CASE_STEP_CHANNEL_ICONS,
  CASE_STEP_STATUS_TONES,
  CASE_URGENCY_TONES,
} from "../theme";

export interface CaseEventsPanelProps {
  events: CentinelaEvent[];
  catalogLabels?: Record<string, string>;
}

export default function CaseEventsPanel({ events, catalogLabels }: CaseEventsPanelProps) {
  const { t, i18n } = useTranslation("common");

  const originLabel = (kind: string) => {
    const origin = toCaseOrigin(kind);
    if (origin === "alerta") return kind;
    return t(`monitoreoIa.caseEvents.origin.${origin}`, { defaultValue: kind });
  };

  const when = (iso: string) =>
    new Date(iso).toLocaleString(i18n.language, {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });

  if (events.length === 0) {
    return (
      <EmptyState
        icon="ri-file-list-3-line"
        title={t("monitoreoIa.caseEvents.empty")}
        description={t("monitoreoIa.caseEvents.emptyDescription")}
      />
    );
  }

  return (
    <ul className="space-y-3">
      {events.map(({ case: item, steps }) => (
        <li key={item.id} className="rounded-xl border border-line bg-surface p-3.5">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[13px] font-bold text-content">
              {catalogLabels?.[item.kind] ?? originLabel(item.kind)}
            </span>
            <Badge tone={CASE_STATUS_TONES[item.status] ?? "neutral"} size="sm">
              {t(`monitoreoIa.caseEvents.status.${item.status}`, {
                defaultValue: item.status,
              })}
            </Badge>
            <Badge tone={CASE_URGENCY_TONES[toCaseUrgency(item.urgency)]} size="sm">
              {t(`monitoreoIa.caseEvents.urgency.${item.urgency}`, {
                defaultValue: item.urgency,
              })}
            </Badge>
            {item.requires_human ? (
              <Badge tone="error" size="sm">
                <i className="ri-user-follow-line text-[9px] max-sm:text-[10px]" />
                {t("monitoreoIa.caseEvents.requiresHuman")}
              </Badge>
            ) : null}
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-3 text-[10px] max-sm:text-[11px] text-content-faint">
            <span className="flex items-center gap-1">
              <i className="ri-time-line text-[9px] max-sm:text-[10px]" />
              {when(item.opened_at)}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-phone-line text-[9px] max-sm:text-[10px]" />
              {t("monitoreoIa.caseEvents.callsCount", { count: item.calls_count })}
            </span>
            {item.theft_score > 0 ? (
              <span className="flex items-center gap-1 font-semibold text-error">
                <i className="ri-alarm-warning-line text-[9px] max-sm:text-[10px]" />
                {t("monitoreoIa.caseEvents.theftScore", { score: item.theft_score })}
              </span>
            ) : null}
          </div>

          {item.resolution ? (
            <p className="mt-2 rounded-lg border border-success/30 bg-success/10 px-3 py-2 text-[11px] leading-relaxed text-success">
              {item.resolution}
            </p>
          ) : null}

          {steps.length > 0 ? (
            <ol className="mt-3 space-y-2 border-t border-line-soft pt-3">
              {steps.map((step) => (
                <li key={`${item.id}-${step.step_index}`} className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-subtle">
                    <i
                      className={`${CASE_STEP_CHANNEL_ICONS[step.channel] ?? "ri-circle-line"} text-[10px] text-content-secondary`}
                    />
                  </span>
                  <div className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-2">
                      <span className="text-[11.5px] font-semibold text-content">
                        {step.label}
                      </span>
                      <Badge tone={CASE_STEP_STATUS_TONES[step.status] ?? "neutral"} size="sm">
                        {t(`monitoreoIa.caseEvents.stepStatus.${step.status}`, {
                          defaultValue: step.status,
                        })}
                      </Badge>
                    </span>
                    {step.detail ? (
                      <p className="mt-0.5 text-[11px] leading-snug text-content-secondary">
                        {step.detail}
                      </p>
                    ) : null}
                    <span className="mt-0.5 block text-[9.5px] max-sm:text-[10px] text-content-faint">
                      {when(step.executed_at)}
                      {step.recipient_name ? ` · ${step.recipient_name}` : ""}
                    </span>
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="mt-3 border-t border-line-soft pt-3 text-[10.5px] italic text-content-faint">
              {t("monitoreoIa.caseEvents.noSteps")}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}
